import { NameHelper } from "@/Common/Helpers/NameHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Patient } from "@/Common/Models/Patient/Patient";
import { RelationshipLinks } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { BritishColumbiaObstetricalFileFamilyHistory } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFileFamilyHistory";
import { BritishColumbiaObstetricalFileInitialPhysicalExamination, IPelvicExamResultExtraInfo } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFileInitialPhysicalExamination";
import { BritishColumbiaObstetricalFilePartnerInformation } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFilePartnerInformation";
import { BritishColumbiaObstetricalFilePatientInformation } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFilePatientInformation";
import { ObstetricalExamResultData } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/ObstetricalExamResultData";
import { BritishColumbiaObstetricalFileInvestigations } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/BritishColumbiaObstetricalFileInvestigations";
import { BritishColumbiaObstetricalFileSignOff } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/BritishColumbiaObstetricalFileSignOff";
import { BritishColumbiaObstetricalFileAboRhFactor } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/Section13/BritishColumbiaObstetricalFileAboRhFactor";
import { BritishColumbiaObstetricalFileTests } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/Section13/BritishColumbiaObstetricalFileTests";
import { ObstetricalExamResultState } from "@/Patient/Models/ObstetricalFile/ObstetricalExamResultState";
import { BaseObstetricalFormCreator, ObstetricalFileParams } from "../BaseObstetricalFormCreator";

class BritishColumbiaObstetricalFileFormCreator extends BaseObstetricalFormCreator {
    private readonly NumberOfInitialsMapping = 3;

    public createObstetricalFileForm(params: ObstetricalFileParams): BritishColumbiaObstetricalFileForm {
        const latestPapTestMedicalItem = params.latestPapTest && new MedicalItemScreeningTest(params.latestPapTest);
        const previousForm = params.previousObstetricalFile?.getFormObject<BritishColumbiaObstetricalFileForm>();

        return new BritishColumbiaObstetricalFileForm({
            Context: params.obstetricFormContext,
            ObstetricalCounters: this.createObstetricalCounters(params.obstetricalCounters),
            Section1: this.getSection1(params.patient),
            Section6: new BritishColumbiaObstetricalFileFamilyHistory(previousForm?.Section6), // Copy Family History section.
            Section10: this.getSection10(params.consultationDate, latestPapTestMedicalItem),
            Section13: this.getSection13(previousForm),
            Section18: this.getSection18()
        });
    }

    private getSection1(patient: Patient): BritishColumbiaObstetricalFilePatientInformation {
        const spouseLookUp = cacheStore.GetRelationshipTypeLookups()?.firstOrDefault(p => p.LookupCode === RelationshipLinks.Spouse);
        const partner = patient.Relationships.firstOrDefault(p => p.RelationLookupId === spouseLookUp?.LookupID);
        const partnerInfo = partner?.Individual;

        // Preferred Family Physician / Nurse Practitioner.
        const physician = patient.IntervenorsOnFile.firstOrDefault(p =>
            (
                p.Model.LicenceOrganisationSpecialisation?.SpecialisationCode == "0" ||
                p.Model.LicenceOrganisationSpecialisation?.NameEn === "Family Medicine"
            ) &&
             p.Model.Intervenor.IsPrimary
        );
        const practicalNurse = patient.IntervenorsOnFile.firstOrDefault(p =>
            p.Model.LicenceOrganisationSpecialisation?.NameEn === "Licenced Practical Nursing" &&
            p.Model.Intervenor?.IsPrimary
        );

        return new BritishColumbiaObstetricalFilePatientInformation({
            PreferredLanguage: cacheStore.GetLookupById(patient?.IndividualResponse?.PreferredSpokenLanguageLookupId)?.GetValue(Locale.en),
            Occupation: patient?.PatientResponse?.Profession,
            PartnerInformation: partnerInfo ? new BritishColumbiaObstetricalFilePartnerInformation({
                FullName: NameHelper.formatFullNameForHasName(partnerInfo),
                Occupation: partner?.Patient?.Profession
            }) : null,
            RelationshipStatus: cacheStore.GetLookupById(patient?.IndividualResponse?.MaritalStatusLookupId)?.GetValue(Locale.en),
            FamilyPhysicianOrNursePractitionerName: physician?.GetName() ?? practicalNurse?.GetName() ?? ""
        });
    }

    private getSection10(consultationDate: Date, latestPapTest: MedicalItemScreeningTest): BritishColumbiaObstetricalFileInitialPhysicalExamination {
        return new BritishColumbiaObstetricalFileInitialPhysicalExamination({
            ExaminationDate: consultationDate,
            PelvicExamResult: this.getPapTest(latestPapTest)
        });
    }

    private getSection13(previousForm: BritishColumbiaObstetricalFileForm): BritishColumbiaObstetricalFileInvestigations {
        return new BritishColumbiaObstetricalFileInvestigations({
            TestResults: new BritishColumbiaObstetricalFileTests({ IsImmuneToRubella: previousForm?.Section13?.TestResults?.IsImmuneToRubella }),
            AboRhFactor: new BritishColumbiaObstetricalFileAboRhFactor({
                BloodGroup: previousForm?.Section13?.AboRhFactor?.BloodGroup,
                BloodRhesus: previousForm?.Section13?.AboRhFactor?.BloodRhesus
            })
        });
    }

    private getPapTest(latestPapTest: MedicalItemScreeningTest): ObstetricalExamResultData<IPelvicExamResultExtraInfo> {
        if (!latestPapTest)
            return new ObstetricalExamResultData<IPelvicExamResultExtraInfo>();

        return new ObstetricalExamResultData<IPelvicExamResultExtraInfo>({
            Result: ObstetricalExamResultState.NotDone,
            ExtraInfo: {
                PapExamCompletionDate: latestPapTest.model.CompletionDate ?? null,
                SexuallyTransmittedInfectionExamDate: null,
                Description: "",
                PapExamResult: latestPapTest.getInterpretationLocalizedValue(Locale.en),
                SexuallyTransmittedInfectionExamValue: ""
            }
        });
    }

    private getSection18(): BritishColumbiaObstetricalFileSignOff[] {
        let signOffs: BritishColumbiaObstetricalFileSignOff[] = [];

        for (let i = 0; i < this.NumberOfInitialsMapping; ++i) {
            signOffs.push(new BritishColumbiaObstetricalFileSignOff());
        }

        return signOffs;
    }
}

export { BritishColumbiaObstetricalFileFormCreator };
